/**
 * Sprawa windykacyjna (TRE-2168, moduł „Należności”).
 *
 * Jedna klientka (e-mail) = jedna sprawa. Pod nią kontrakty (rekordy dostępu, których raty
 * składają się na saldo), oś czasu zdarzeń (automat vs ręcznie) i kolejka zadań dla
 * administracji. Sprawa żyje od pierwszej nieopłaconej raty do spłaty, ugody albo umorzenia —
 * także po przekazaniu do windykacji zewnętrznej i po zakończeniu kontraktu (monitoring).
 */

/** Gdzie sprawa jest w procesie. Osobno od `CollectionsSituation` (dlaczego nie płaci). */
export const CollectionsCaseStatus = {
  /** Zaległość wykryta, nikt jeszcze nic nie zrobił. */
  New: "new",
  /** Przypomnienia poszły, trwają telefony. */
  Recovering: "recovering",
  /** Nie odbiera, czekamy na oddzwonienie / odpowiedź. */
  AwaitingContact: "awaiting_contact",
  /** Klientka zadeklarowała wpłatę w terminie — automat wstrzymany do tego dnia. */
  AwaitingPayment: "awaiting_payment",
  /** Wysłano formalne wezwanie (lub ostateczne). */
  DemandSent: "demand_sent",
  /** Sprawa u firmy windykacyjnej; telefony administracji wstrzymane, saldo liczone dalej. */
  ExternalCollection: "external_collection",
  /** Uzgodniony nowy harmonogram; dopóki jest realizowany, nie windykujemy. */
  Settlement: "settlement",
  /** Kontrakt się skończył, dług został; przeglądy co 90 dni zamiast telefonów. */
  Monitoring: "monitoring",
  ClosedPaid: "closed_paid",
  ClosedSettled: "closed_settled",
  ClosedWrittenOff: "closed_written_off",
} as const;

export type CollectionsCaseStatus = (typeof CollectionsCaseStatus)[keyof typeof CollectionsCaseStatus];

export const COLLECTIONS_CASE_STATUS_LABELS: Record<CollectionsCaseStatus, string> = {
  new: "Nowa zaległość",
  recovering: "Odzyskiwanie płatności",
  awaiting_contact: "Oczekiwanie na kontakt",
  awaiting_payment: "Oczekiwanie na płatność",
  demand_sent: "Wezwanie do zapłaty",
  external_collection: "Windykacja zewnętrzna",
  settlement: "Ugoda",
  monitoring: "Monitoring długoterminowy",
  closed_paid: "Zakończona — opłacona",
  closed_settled: "Zakończona — ugoda",
  closed_written_off: "Zakończona — umorzenie",
};

export const COLLECTIONS_CASE_CLOSED_STATUSES: readonly CollectionsCaseStatus[] = [
  CollectionsCaseStatus.ClosedPaid,
  CollectionsCaseStatus.ClosedSettled,
  CollectionsCaseStatus.ClosedWrittenOff,
];

/**
 * Statuses that need their own data (a declaration, a settlement, an agency handover)
 * and the tasks that release them — only the dedicated actions may set them, never a
 * bare status change, or the case would sit on hold with nothing to end it.
 */
export const COLLECTIONS_CASE_WORKFLOW_STATUSES: readonly CollectionsCaseStatus[] = [
  CollectionsCaseStatus.AwaitingPayment,
  CollectionsCaseStatus.Settlement,
  CollectionsCaseStatus.ExternalCollection,
];

/** Statusy, przy których automatyczna drabinka i telefony milczą. */
export const COLLECTIONS_CASE_STATUSES_ON_HOLD: readonly CollectionsCaseStatus[] = [
  CollectionsCaseStatus.AwaitingPayment,
  CollectionsCaseStatus.Settlement,
  CollectionsCaseStatus.ExternalCollection,
  CollectionsCaseStatus.Monitoring,
  ...COLLECTIONS_CASE_CLOSED_STATUSES,
];

/** Dlaczego klientka nie płaci — ustawiane z wyniku rozmowy. */
export const CollectionsSituation = {
  FinancialProblem: "financial_problem",
  WantsToQuit: "wants_to_quit",
  Unhappy: "unhappy",
  Dispute: "dispute",
  Special: "special",
  NoContact: "no_contact",
  NoPhone: "no_phone",
} as const;

export type CollectionsSituation = (typeof CollectionsSituation)[keyof typeof CollectionsSituation];

export const COLLECTIONS_SITUATION_LABELS: Record<CollectionsSituation, string> = {
  financial_problem: "Problem finansowy",
  wants_to_quit: "Chce zrezygnować",
  unhappy: "Niezadowolona z programu",
  dispute: "Reklamacja / spór",
  special: "Sytuacja szczególna",
  no_contact: "Brak kontaktu",
  no_phone: "Brak numeru / numer nieaktualny",
};

/** Wynik telefonu — lista z briefu CEO plus „brak numeru”. */
export const CollectionsCallOutcome = {
  Answered: "answered",
  NoAnswer: "no_answer",
  CallBackLater: "call_back_later",
  PaymentArranged: "payment_arranged",
  FinancialProblem: "financial_problem",
  WantsToQuit: "wants_to_quit",
  Unhappy: "unhappy",
  Complaint: "complaint",
  Special: "special",
  NoPhone: "no_phone",
  Other: "other",
} as const;

export type CollectionsCallOutcome = (typeof CollectionsCallOutcome)[keyof typeof CollectionsCallOutcome];

export const COLLECTIONS_CALL_OUTCOME_LABELS: Record<CollectionsCallOutcome, string> = {
  answered: "Odebrała",
  no_answer: "Brak odpowiedzi",
  call_back_later: "Oddzwoni później",
  payment_arranged: "Ustalono płatność",
  financial_problem: "Problem finansowy",
  wants_to_quit: "Chce zrezygnować",
  unhappy: "Niezadowolona",
  complaint: "Reklamacja",
  special: "Sytuacja szczególna",
  no_phone: "Brak numeru / numer nieaktualny",
  other: "Inne",
};

/** Wynik rozmowy → sytuacja sprawy (gdy rozmowa coś o niej mówi). */
export const CALL_OUTCOME_TO_SITUATION: Partial<Record<CollectionsCallOutcome, CollectionsSituation>> = {
  financial_problem: CollectionsSituation.FinancialProblem,
  wants_to_quit: CollectionsSituation.WantsToQuit,
  unhappy: CollectionsSituation.Unhappy,
  complaint: CollectionsSituation.Dispute,
  special: CollectionsSituation.Special,
  no_answer: CollectionsSituation.NoContact,
  no_phone: CollectionsSituation.NoPhone,
};

export const CollectionsTaskType = {
  Call1: "call_1",
  Call2: "call_2",
  ProposeSolution: "propose_solution",
  CheckDeclaration: "check_declaration",
  BrokenDeclaration: "broken_declaration",
  LastSoftAttempt: "last_soft_attempt",
  SendLetter: "send_letter",
  /** Decyzja po ostatecznym wezwaniu: Vindicat, ustalenia albo umorzenie. */
  Decision: "decision",
  ReviewD60: "review_d60",
  ReviewD90: "review_d90",
  ReviewD120: "review_d120",
  FinalReview: "final_review",
  LongTermReview: "long_term_review",
  TransferToVerify: "transfer_to_verify",
} as const;

export type CollectionsTaskType = (typeof CollectionsTaskType)[keyof typeof CollectionsTaskType];

export const COLLECTIONS_TASK_TYPE_LABELS: Record<CollectionsTaskType, string> = {
  call_1: "Telefon #1",
  call_2: "Telefon #2",
  propose_solution: "Propozycja rozwiązania",
  check_declaration: "Sprawdzenie deklaracji płatności",
  broken_declaration: "Złamana deklaracja płatności",
  last_soft_attempt: "Ostatnia próba polubowna",
  send_letter: "Nadanie listu poleconego",
  decision: "Decyzja: Vindicat / ugoda / umorzenie",
  review_d60: "Przegląd D60",
  review_d90: "Przegląd D90",
  review_d120: "Przegląd D120",
  final_review: "Finalny przegląd zobowiązania",
  long_term_review: "Przegląd starej należności",
  transfer_to_verify: "Przelew do weryfikacji",
};

/** Typy zadań, które zamyka się wynikiem rozmowy. */
export const COLLECTIONS_CALL_TASK_TYPES: readonly CollectionsTaskType[] = [
  CollectionsTaskType.Call1,
  CollectionsTaskType.Call2,
  CollectionsTaskType.LastSoftAttempt,
];

export const CollectionsTaskStatus = { Open: "open", Done: "done", Canceled: "canceled" } as const;
export type CollectionsTaskStatus = (typeof CollectionsTaskStatus)[keyof typeof CollectionsTaskStatus];

export const CollectionsEventKind = {
  CaseOpened: "case_opened",
  CaseReopened: "case_reopened",
  InstallmentAdded: "installment_added",
  InvoiceClosed: "invoice_closed",
  PaymentReceived: "payment_received",
  EmailSent: "email_sent",
  SmsSent: "sms_sent",
  LetterGenerated: "letter_generated",
  TaskCompleted: "task_completed",
  StatusChanged: "status_changed",
  SituationChanged: "situation_changed",
  DeclarationSet: "declaration_set",
  DeclarationKept: "declaration_kept",
  DeclarationBroken: "declaration_broken",
  SettlementSet: "settlement_set",
  SettlementBroken: "settlement_broken",
  HandedToAgency: "handed_to_agency",
  OwnerChanged: "owner_changed",
  Note: "note",
  /** Automat ruszył na tej sprawie (od tego dnia liczy się drabinka). */
  AutomationStarted: "automation_started",
} as const;

export type CollectionsEventKind = (typeof CollectionsEventKind)[keyof typeof CollectionsEventKind];

/** Polskie etykiety zdarzeń na osi czasu sprawy. */
export const COLLECTIONS_EVENT_KIND_LABELS: Record<CollectionsEventKind, string> = {
  case_opened: "Sprawa otwarta",
  case_reopened: "Sprawa wznowiona",
  installment_added: "Nowa rata wymagalna",
  invoice_closed: "Faktura zamknięta po stronie Stripe",
  payment_received: "Wpłata",
  email_sent: "Mail wysłany",
  sms_sent: "SMS wysłany",
  letter_generated: "Pismo wygenerowane",
  task_completed: "Zadanie wykonane",
  status_changed: "Zmiana statusu",
  situation_changed: "Zmiana sytuacji",
  declaration_set: "Deklaracja płatności",
  declaration_kept: "Deklaracja dotrzymana",
  declaration_broken: "Złamana deklaracja",
  settlement_set: "Ugoda ustalona",
  settlement_broken: "Złamana ugoda",
  handed_to_agency: "Przekazanie do windykacji zewnętrznej",
  owner_changed: "Zmiana opiekuna",
  note: "Notatka",
  automation_started: "Start automatycznych wiadomości",
};

export const CollectionsEventSource = { System: "system", Automation: "automation", User: "user" } as const;
export type CollectionsEventSource = (typeof CollectionsEventSource)[keyof typeof CollectionsEventSource];

export interface CollectionsPaymentDeclaration {
  /** grosze */
  amount: number;
  /** ISO date */
  dueAt: string;
  createdAt: string;
  createdBy: string;
  outcome: "pending" | "kept" | "broken";
  checkedAt?: string | null;
}

export interface CollectionsSettlement {
  originalInstallments: number | null;
  /** grosze */
  originalAmount: number | null;
  installments: number;
  /** grosze */
  amount: number;
  createdAt: string;
  createdBy: string;
  /** `changeId` z ChangeInstallmentPlan — ugoda dla kart to realna zmiana harmonogramu w Stripe. */
  changeId?: string | null;
  status: "active" | "kept" | "broken";
}

export interface CollectionsExternalCollection {
  agency: string;
  handedAt: string;
  handedBy: string;
  reference?: string | null;
  note?: string | null;
}

/** Saldo denormalizowane przy synchronizacji z faktur; kwoty w groszach. */
export interface CollectionsCaseBalance {
  overdueAmount: number;
  overdueInvoices: number;
  overdueInvoiceIds: string[];
  /** Faktury kartą, które Stripe jeszcze ponawia — nie w zaległości, ale widoczne. */
  retryingAmount: number;
  retryingInvoices: number;
  /** Stripe id faktur w ponowieniu — sync po nich wykrywa, że ponowienie się udało. */
  retryingInvoiceIds?: string[];
  /** Raty, które dopiero zapadną (plan minus wystawione), łącznie po kontraktach. */
  futureInstallments: number;
  futureAmount: number;
  /** overdueAmount + futureAmount */
  remainingContractAmount: number;
  oldestDueAt: string | null;
  daysOverdue: number;
  /** Czy którykolwiek kontrakt jest zarchiwizowany. */
  hasArchivedContract: boolean;
  syncedAt: string;
}

export interface CollectionsCase {
  id: string;
  createdAt: string;
  updatedAt: string;
  clientEmail: string;
  customerId: string | null;
  status: CollectionsCaseStatus;
  situation: CollectionsSituation | null;
  ownerId: string | null;
  openedAt: string;
  closedAt: string | null;
  declaration: CollectionsPaymentDeclaration | null;
  settlement: CollectionsSettlement | null;
  externalCollection: CollectionsExternalCollection | null;
  balance: CollectionsCaseBalance;
  /** Stan drabinki (TRE-2169); null = automat jeszcze nie dotknął tej sprawy. */
  automation?: CollectionsAutomationState | null;
}

export interface CollectionsCaseContract {
  id: string;
  caseId: string;
  productClientId: string;
  productId: string;
  kind: "original" | "extension" | "settlement";
  addedAt: string;
}

export interface CollectionsEvent {
  id: string;
  caseId: string;
  occurredAt: string;
  kind: CollectionsEventKind;
  source: CollectionsEventSource;
  actorId: string | null;
  payload: Record<string, unknown>;
  dedupKey: string | null;
}

export interface CollectionsTask {
  id: string;
  caseId: string;
  createdAt: string;
  type: CollectionsTaskType;
  /** ISO date (YYYY-MM-DD) */
  dueAt: string;
  status: CollectionsTaskStatus;
  assigneeId: string | null;
  doneAt: string | null;
  doneBy: string | null;
  outcome: string | null;
  note: string | null;
  dedupKey: string | null;
}

/**
 * Kroki drabinki windykacyjnej (TRE-2169). Litery z propozycji v1: K = karta,
 * M = przelew (manual), W = wezwania wspólne dla obu, N = nowa rata dołączyła,
 * P = rozliczone, D = decyzja (zadanie, bez wiadomości).
 */
export const CollectionsStep = {
  K1: "K1",
  K2: "K2",
  K3: "K3",
  M1: "M1",
  M2: "M2",
  M3: "M3",
  W1: "W1",
  W2: "W2",
  D: "D",
  N: "N",
  P: "P",
  /** Wiadomość własna: draft asystenta AI albo tekst Anity, wysłane z karty sprawy (TRE-2171). */
  C: "C",
} as const;

export type CollectionsStep = (typeof CollectionsStep)[keyof typeof CollectionsStep];

export const COLLECTIONS_STEP_LABELS: Record<CollectionsStep, string> = {
  K1: "Nie udało się pobrać raty z karty",
  K2: "Nadal nie udało się pobrać raty",
  K3: "Bank odrzucił ratę, ponowień już nie będzie",
  M1: "Przypomnienie o racie przed terminem",
  M2: "Termin przelewu minął",
  M3: "Tydzień po terminie przelewu",
  W1: "Wezwanie do zapłaty",
  W2: "Ostateczne wezwanie do zapłaty",
  D: "Decyzja po ostatecznym wezwaniu",
  N: "Nowa rata dołączyła do zaległości",
  P: "Rozliczone",
  C: "Wiadomość własna",
};

/** Kroki drabinki w kolejności eskalacji; wysyłamy tylko najdalszy należny. */
export const COLLECTIONS_CARD_LADDER: readonly CollectionsStep[] = ["K1", "K2", "K3", "W1", "W2", "D"];
export const COLLECTIONS_MANUAL_LADDER: readonly CollectionsStep[] = ["M2", "M3", "W1", "W2", "D"];

/** Which family of automatic messages a step belongs to; `null` = never automatic (a message a person wrote). */
export type CollectionsAutomationGroup = "info" | "reminders" | "demands";

/**
 * What the automatic messages may do — set by the owner in the Należności tab
 * (TRE-2169). Everything off by default: nothing goes out until someone
 * switches it on deliberately.
 */
export interface CollectionsAutomationSettings {
  /** Nieudane pobranie / termin przelewu / nowa rata / rozliczone — informacje bez ponaglania. */
  info: boolean;
  /** Ponowienia wyczerpane, tydzień po terminie — plus zadanie telefonu dla administracji. */
  reminders: boolean;
  /** Wezwanie i ostateczne wezwanie do zapłaty z pismem PDF, potem zadanie decyzji. */
  demands: boolean;
  /** SMS obok maila tam, gdzie krok go przewiduje (9:00–19:00). */
  sms: boolean;
}

/**
 * Who signs the collections mails, SMS and demand letters and where the client
 * replies. The owner edits it in the Należności tab; the service config only
 * fills what the row leaves empty.
 */
export interface CollectionsSignature {
  /** „Anita" — the signature and the name in SMS. */
  name: string;
  /** The line under the name, e.g. „Dział rozliczeń, Program Hollywood". */
  title: string;
  /** Reply-to of every mail and the address the letter points to. */
  email: string;
  phone: string | null;
}

export const DEFAULT_COLLECTIONS_SIGNATURE_TITLE = "Dział rozliczeń, Program Hollywood";

export const DEFAULT_COLLECTIONS_AUTOMATION_SETTINGS: CollectionsAutomationSettings = {
  info: false,
  reminders: false,
  demands: false,
  sms: false,
};

export const COLLECTIONS_AUTOMATION_SETTING_LABELS: Record<
  keyof CollectionsAutomationSettings,
  { label: string; description: string }
> = {
  info: {
    label: "Informacje o racie",
    description:
      "Mail, gdy nie udało się pobrać raty z karty (i ponownie po tygodniu), przypomnienie dwa dni przed terminem przelewu i mail, że termin minął, informacja o nowej racie w zaległości oraz podziękowanie po wpłacie. Bez straszenia.",
  },
  reminders: {
    label: "Przypomnienia",
    description:
      "Gdy bank odrzuci ratę na dobre albo przelew jest tydzień po terminie: mail z prośbą o kontakt i zadanie telefonu dla administracji w kolejce „Do zrobienia dzisiaj”.",
  },
  demands: {
    label: "Wezwania do zapłaty",
    description:
      "Dwa tygodnie po przypomnieniu wezwanie do zapłaty z pismem PDF (termin 7 dni), po kolejnych dwóch ostateczne wezwanie, a potem zadanie decyzji: windykacja zewnętrzna, ugoda albo umorzenie.",
  },
  sms: {
    label: "SMS-y",
    description:
      "Krótki SMS obok maila przy przypomnieniach i wezwaniach, wysyłany tylko między 9:00 a 19:00. Wymaga skonfigurowanego SMSAPI.",
  },
};

/** Which switch lets a step out. */
export const COLLECTIONS_STEP_GROUP: Record<CollectionsStep, CollectionsAutomationGroup | null> = {
  K1: "info",
  K2: "info",
  M1: "info",
  M2: "info",
  N: "info",
  P: "info",
  K3: "reminders",
  M3: "reminders",
  W1: "demands",
  W2: "demands",
  D: "demands",
  C: null,
};

export const isCollectionsAutomationOn = (settings: CollectionsAutomationSettings): boolean =>
  settings.info || settings.reminders || settings.demands;

/** „Informacje o racie, Przypomnienia, SMS-y" — or „wyłączone". */
export const describeCollectionsAutomation = (settings: CollectionsAutomationSettings | null | undefined): string => {
  if (!settings) return "wyłączone";
  const on = (Object.keys(COLLECTIONS_AUTOMATION_SETTING_LABELS) as (keyof CollectionsAutomationSettings)[])
    .filter((key) => settings[key])
    .map((key) => COLLECTIONS_AUTOMATION_SETTING_LABELS[key].label.toLowerCase());
  return on.length ? on.join(", ") : "wyłączone";
};

export type CollectionsLadder = "card" | "manual";

/** Stan drabinki zapisany na sprawie — źródło prawdy dla „który krok już poszedł". */
export interface CollectionsAutomationState {
  /** ISO date — dzień, w którym automat pierwszy raz zobaczył sprawę z włączonym poziomem. */
  startedAt: string;
  ladder: CollectionsLadder;
  /** Faktura, od której liczą się dni (najstarsza nieopłacona w chwili startu / ostatniego kroku). */
  anchorInvoiceId: string | null;
  /** ISO date — D0 drabinki po uwzględnieniu startu automatu. */
  anchorDueAt: string | null;
  /** Krok → ISO date wysyłki. */
  steps: Partial<Record<CollectionsStep, string>>;
  /** ISO datetime ostatniego przebiegu, który dotknął sprawy. */
  lastRunAt: string;
}

export const CollectionsMessageChannel = { Email: "email", Sms: "sms", Letter: "letter" } as const;
export type CollectionsMessageChannel = (typeof CollectionsMessageChannel)[keyof typeof CollectionsMessageChannel];

/** `pending` = klucz zarezerwowany przed wysyłką; równoległy przebieg go nie zdubluje (TRE-2169). */
export const CollectionsMessageStatus = {
  Pending: "pending",
  Sent: "sent",
  Skipped: "skipped",
  Failed: "failed",
} as const;
export type CollectionsMessageStatus = (typeof CollectionsMessageStatus)[keyof typeof CollectionsMessageStatus];

/**
 * Log wysyłek automatu. Osobno od zdarzeń sprawy, bo M1 (przypomnienie przed
 * terminem) idzie zanim sprawa istnieje, a klucz idempotencji musi przeżyć
 * zamknięcie i ponowne otwarcie sprawy.
 */
export interface CollectionsMessage {
  id: string;
  createdAt: string;
  caseId: string | null;
  clientEmail: string;
  step: CollectionsStep;
  channel: CollectionsMessageChannel;
  /** Alias szablonu Postmark albo nazwa szablonu SMS / pisma. */
  template: string;
  /** Faktury, których dotyczyła wiadomość. */
  invoiceIds: string[];
  status: CollectionsMessageStatus;
  error: string | null;
  /** Adresat (e-mail / numer) i model użyty do renderu — do podglądu w panelu. */
  payload: Record<string, unknown>;
  dedupKey: string;
}
